import { ModuleOverviewHeader } from "../../v2/module/ModuleOverviewHeader";
import { generateGtmEvent } from "../../../Helpers";
import { Box, Typography } from "@mui/material";
import { SkillVideoModal } from "../../v2/SkillVideoModal";
import { useContext, useState } from "react";
import { Question, Target, TargetSkill as TargetSkillTree } from "../../utils/CourseTree";
import { CourseStructureContext } from "../../../context/CourseStructureContext";
import { CourseStructureCtx, UserCtx } from "../../../interfaces";
import { useHistory } from "react-router-dom";
import { LearningTargetCard } from "../../v2/module/LearningTargetCard";
import { BioSpineLessonCard } from "../../v2/module/BioSpineLessonCard";
import { UserContext } from "../../../context/UserContext";
import { Helmet } from "react-helmet-async";
import { CourseMetadata } from "@backend-types";
import { ModuleTargetGroupList } from "../../v2/module/ModuleTargetGroupList";
import axios from "../../../services/axios";
import { Role } from "../../../enums";
import { MacsAuthenticator } from "../../../hooks/useMacsAuthenticator";
import { ButtonZoom } from "../../v2/utils/ButtonZoom";

interface ModuleContentProps {
  moduleId: string;
}

export const ModuleContent = (props: ModuleContentProps) => {
  const history = useHistory();
  const userCtx = useContext(UserContext) as UserCtx;
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const { moduleId } = props;
  let currentModule = courseStructureCtx.courseTreeStructure?.getModule(moduleId);
  let currentCourse = courseStructureCtx.courseTreeStructure?.getCurrentCourse();
  let moduleTargets: Target[] = currentModule?.getAllTargets() || [];
  let moduleTargetGroups = currentModule?.target_groups ?? [];
  const courseMetadata = currentCourse?.getMetadata() as CourseMetadata;
  const currentModuleName = currentModule?.getFormattedModuleName();

  const [skillVideoOpen, setSkillVideoModalOpen] = useState(false);
  const [skillVideoSkills, setSkillsVideoSkills] = useState<TargetSkillTree[]>();
  const handleOpenSkillVideoModal = (skills: TargetSkillTree[] | undefined) => {
    if (!skills) return;
    setSkillVideoModalOpen(true);
    setSkillsVideoSkills(skills);
  };
  return (
    <>
      <Helmet>
        <title> {currentModuleName!} | Digit</title>
      </Helmet>
      <Box sx={{ maxWidth: 846 }}>
        <ModuleOverviewHeader
          backButtonLabel={"All modules"}
          onBackClick={() => {
            generateGtmEvent({
              event: "link",
              action: "click",
              name: "onclick",
              type: "internal link",
              region: "main content",
              section: `${currentModuleName! + " ^ module activities"}`,
              text: "all modules",
            });
            history.push({
              pathname: `/explore/activities`,
              search: `?courseId=${currentCourse?.id}`,
            });
          }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <Typography
              variant="h3"
              component={"span"}
              sx={{ fontWeight: "bold" }}
              tabIndex={0}
              aria-label={currentModuleName}>
              {currentModuleName!}
            </Typography>
            {(userCtx.dbUser?.role === Role.teacher ||
              userCtx.dbUser.role === Role.admin ||
              userCtx.dbUser.role === Role.super) && (
              <ButtonZoom
                size="large"
                variant="contained"
                aria-label={"View this course as a student, button, not pressed."}
                sx={{
                  minWidth: "242px",
                  "&:active": {
                    border: "2px solid #191919",
                  },
                }}
                onClick={async () => {
                  try {
                    const fullUrl = window.location.href;
                    MacsAuthenticator.authHook.setExitStudentViewPath(fullUrl);

                    const res = await axios.post(`/user/start-student-view`, null, {
                      params: {
                        launchedCourseId: currentCourse?.id,
                        ltik: MacsAuthenticator.authHook.ltik,
                      },
                    });

                    if (res.status === 200) {
                      const newClassId = res.data?.newClassId;
                      const launchedAssignmentId = res.data?.launchedAssignmentId;
                      if (launchedAssignmentId)
                        MacsAuthenticator.authHook.setLaunchedAssignmentId(
                          launchedAssignmentId
                        );
                      MacsAuthenticator.authHook.setIsStudentView("true");
                      //Set the role on the frontend temporarily
                      userCtx.dbUser.role = Role.student;
                      if (currentModule) {
                        history.push({
                          pathname: "/modules",
                          search: new URLSearchParams({
                            classId: newClassId,
                            moduleId: currentModule.id,
                          }).toString(),
                        });
                      } else {
                        history.push({
                          pathname: "/learning-overview",
                          search: new URLSearchParams({
                            classId: newClassId,
                          }).toString(),
                        });
                      }
                      window.location.reload();
                    }
                  } catch (e: any) {
                    console.error("Failed to start student view", e);
                    throw e;
                  }
                }}>
                <Typography variant="h2" noWrap component="span" fontSize="19px">
                  <i
                    className="fa-regular fa-graduation-cap "
                    style={{ marginRight: "16px" }}></i>
                  View as student
                </Typography>
              </ButtonZoom>
            )}
          </Box>
        </ModuleOverviewHeader>
        <Box sx={{ marginTop: "32px" }}>
          {moduleTargetGroups.length > 0 && (
            <ModuleTargetGroupList
              targetGroups={moduleTargetGroups}
              targets={moduleTargets}
              openSkillVideoModal={handleOpenSkillVideoModal}
              languageOverrides={courseMetadata?.language_overrides}
            />
          )}
          {moduleTargetGroups.length < 1 &&
            moduleTargets &&
            moduleTargets.map((target, index) => {
              const questions = target.getTargetQuestions() as Question[];
              const activityTypes = questions.map((item) => item.activity_type);
              const targetQuizScores = userCtx.userQuizSubmissions.filter(
                (item) => item.target_id === target.id
              );

              if (activityTypes.includes("orchard_node")) {
                return (
                  <BioSpineLessonCard
                    cardVariant={"teacher"}
                    status={target.getTargetCompletionStatus()}
                    target={target}
                    targetQuizSubmissions={targetQuizScores}
                    key={target.id}
                    last={moduleTargets.length - 1 === index}
                    resumeQuestion={target.getFirstQuestion()}
                    openSkillVideoModal={() =>
                      handleOpenSkillVideoModal(
                        courseStructureCtx.courseTreeStructure?.getTargetSkills(target.id)
                      )
                    }
                  />
                );
              } else {
                return (
                  <LearningTargetCard
                    cardVariant={"teacher"}
                    status={target.getTargetCompletionStatus()}
                    target={target}
                    key={target.id}
                    last={moduleTargets.length - 1 === index}
                    resumeQuestion={target.getFirstQuestion()}
                    openSkillVideoModal={() =>
                      handleOpenSkillVideoModal(
                        courseStructureCtx.courseTreeStructure?.getTargetSkills(target.id)
                      )
                    }
                  />
                );
              }
            })}
        </Box>
      </Box>
      <SkillVideoModal
        open={skillVideoOpen}
        skills={skillVideoSkills}
        onClose={() => setSkillVideoModalOpen(false)}
        showControls={true}
        gtmSection={currentModuleName!}
        onClickViewAllVideos={() => {
          history.push({
            pathname: "/explore/browse",
            search: `?courseId=${currentCourse?.id}`,
          });
        }}
      />
    </>
  );
};
